import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { SITE_INFO } from '../data/siteData'
import { TestimonialsSlider } from '../components/TestimonialsSlider'
import { ContactForm } from '../components/ContactForm'
import { useCMSSide, useVirksomhedsoplysninger } from '../hooks/useCMS'

export function OmBoPage() {
  const { side: cmsSide } = useCMSSide('om-bo')
  const { info } = useVirksomhedsoplysninger()

  useSEO({
    title: cmsSide?.seo_titel || 'OM BO | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Lær Bo Steendahl at kende - Totum Kropsterapeut, Hypnotherapy Practitioner og Mindset Coach. Læs om hans baggrund, tilgang og livssyn.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20170831-o2q9154-2.jpg',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Mød din behandler
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Om Bo Steendahl'}
          </h1>
          <p className="mt-3 text-lg sm:text-xl font-medium text-[#7c875a]">
            {cmsSide?.hero_underoverskrift || 'Totum Kropsterapeut · Hypnotherapy Practitioner · Mindset Coach'}
          </p>
        </div>
      </section>

      {/* Main Story & Scrape Text */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-10">
              {/* Portrait and Introduction Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white">
                <img
                  src="https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20220903-135741.jpg"
                  alt="Bo Steendahl"
                  className="w-full h-[400px] object-cover object-top"
                />
              </div>
            </div>
            <div className="md:col-span-7 space-y-4">
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                Et andet livssyn og forståelse af kroppen
              </h2>
              <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                Jeg har siden mine helt unge år været interesseret i den asiatiske livs-opfattelse og forståelse af kroppen.
              </p>
              <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                Grupper af mennesker som i harmoni lave rolige og bløde bevægelser med fokuseret kontrol i offentlige parker trak i mig som en magnet.
              </p>
              <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                Jeg læste om akupressur og zoneterapi, og jeg øvede mig på venner og bekendte. Jeg tog kurser i kinesiologi og øre-akupunktur, Anatomi &amp; Fysiologi og Sygdomslære.
              </p>
            </div>
          </div>

          {/* Mødet med Svein og NLP */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Mødet med NLP og samspillet mellem sind og sprog
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Interessen var der men livet førte mig på en anden vej, men igen og igen oplevede jeg at der var noget som blev ved med at banke på min dør.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Fra 2007 og 2,5 år frem arbejde jeg i Norge. Helt tilfældigt lærte en Nordmand at kende, som manglede en gast ombord på sit sejlskib når han deltog i kapsejlads i den lokale sejlklub.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Svein, som er 30 år ældre end mig blev min ven. Bum og 15 sekunder efter var vi venner. Nogen gange er vi så heldige at møder et menneske, som på en eller anden måde bare forstå en uden at man skal forklarer sig - "vi var på bølgelængde". Svein havde gennem mange år arbejdet med og undervist i NLP (Neuro Lingvistisk programmering). Han lærte mig om den fundamentale dynamik mellem sind og sprog, og hvordan samspillet påvirker vores krop, adfærd og tanker.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Efter min tid i Norge, kom jeg til Sverige hvor jeg arbejde i 4 år. På en flyrejse hjem til Danmark finder jeg en stak udskrifter i lommen med blade og magasiner. Udskrifterne var om NLP, hvilket jeg på det tidspunkt fandt noget pussigt. Der var noget her jeg blev nød til at undersøge nærmere. Specielt det her med sammenhængen mellem krop, sind og tanker synes jeg var interessant.
            </p>
          </div>

          {/* Beslutningen i 2018 & Totum Kropsterapi */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Beslutningen om Totum Kropsterapi
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              I sommeren 2018 tog jeg så beslutningen - Nu skulle det være.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Jeg havde flere gange stødt på noget om Totum Kropsterapeut. Jeg havde ikke tidligere hørt om kropsterapi, men fandt hurtigt ud af at det præcist handlede om sammenhængen mellem krop og psyke. Jeg deltog på et info møde men havde allerede besluttet mig på forhånd - Det er det her jeg har ledt efter. Det var det håndværk jeg skulle lære så jeg blev i stand til at gøre det jeg inderst inde altid havde drømt om:
            </p>
            <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-base sm:text-lg italic text-[#1b3a4b] font-serif leading-relaxed">
              At støtte, udfordre, inspirere og hjælpe de mennesker jeg møder på min vej.
            </blockquote>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              At uddannelsen i den grad så har flyttet rundt på nogle af grundpillerne i mit indre havde jeg ikke forventet, men jeg er meget taknemlig for den udvikling jeg har været i gennem. Jeg har mærket på egen krop og sind hvad det er Kropsterapi kan skabe af forandring.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Jeg ved at jeg med mit håndværk som Totum Kropsterapeut, og med min indlevelse som menneske med en solid livserfaring, gør en forskel for mine klienter.
            </p>
          </div>

          {/* Hvad Bo ser hos sine klienter */}
          <div className="bg-[#172e3b] text-white rounded-2xl p-8 sm:p-10 shadow-xl border border-white/10 space-y-4">
            <h3 className="font-serif text-2xl font-bold text-white">
              Jeg har efterhånden set det mange gange
            </h3>
            <div className="space-y-3 text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
              <p>
                Set hvordan mine klienters kroppe har kæmpet for at holde fast i gamle spændinger. Set mine klienter slipper fri af gamle traumer som de har samlet sammen og slæbt rundt på i adskellige år.
              </p>
              <p>
                Set det blik af forløsning og åbenbaring når de mærker kroppen give slip. Set energien vende tilbage. Set håbet og drømmen få liv. Set troen og tilliden til at det umulige, igen blive muligt.
              </p>
              <p>
                Et liv hvor smerten fra det som var før, bliver omdannet til et ar, som et minde om det som var, men som nu ikke længere eksistere.
              </p>
              <p className="font-medium text-white">
                Et liv hvor der er plads til at vokse og mærke glæde, som bobler i maven. Det er den følelse jeg brænder for at hjælpe mine klienter med at finde.
              </p>
              <p className="text-[#7c875a] font-serif text-lg font-bold pt-2">
                Det er muligt - også for dig.
              </p>
            </div>
          </div>

              {/* Dybdegående undersider / links */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4">
                <Link
                  to="/mine-9-grundprincipper"
                  className="bg-white p-6 rounded-2xl border border-[#E5E0D8] hover:border-[#1b3a4b] shadow-sm hover:shadow transition-all group"
                >
                  <i className="fa-solid fa-list-check text-2xl text-[#7c875a] mb-3 block group-hover:scale-110 transition-transform" />
                  <h4 className="font-serif font-bold text-lg text-[#1b3a4b] mb-1">9 Grundprincipper</h4>
                  <p className="text-xs text-[#64748B]">Læs om de grundlæggende principper i min tilgang til klienter.</p>
                </Link>

                <Link
                  to="/klienthistorie"
                  className="bg-white p-6 rounded-2xl border border-[#E5E0D8] hover:border-[#1b3a4b] shadow-sm hover:shadow transition-all group"
                >
                  <i className="fa-solid fa-book-open-reader text-2xl text-[#7c875a] mb-3 block group-hover:scale-110 transition-transform" />
                  <h4 className="font-serif font-bold text-lg text-[#1b3a4b]">Klienthistorie</h4>
                  <p className="text-xs text-[#64748B] mt-1">Læs Charlottes bevægende historie om da sorgen gav slip i hoften.</p>
                </Link>

                <Link
                  to="/uddannelse"
                  className="bg-white p-6 rounded-2xl border border-[#E5E0D8] hover:border-[#1b3a4b] shadow-sm hover:shadow transition-all group"
                >
                  <i className="fa-solid fa-certificate text-2xl text-[#7c875a] mb-3 block group-hover:scale-110 transition-transform" />
                  <h4 className="font-serif font-bold text-lg text-[#1b3a4b]">Dokumenter</h4>
                  <p className="text-xs text-[#64748B] mt-1">Se RAB-registrering, uddannelsesbeviser og etiske standarder.</p>
                </Link>
              </div>
            </div>

            {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Anmeldelser Slider */}
      <TestimonialsSlider />
    </div>
  )
}
